class ProductDetailVariation extends HTMLElement {
  // Cross-instance cache of section HTML, keyed by `${sectionId}:${variantId}`.
  // Holds the in-flight fetch Promise so concurrent requests for the same variant
  // (e.g. prefetch on hover then real click) share a single network request.
  static _cache = new Map();
  static _CACHE_MAX = 80; // ~one entry per fabric × a few size combos, comfortably

  // De-dupe `<link rel=preload>` image tags so we don't pile them up on each
  // option change. Set holds normalized image URLs.
  static _preloadedImages = new Set();

  // What the page shows, per section: the variant id, and the section HTML the
  // server rendered for it. A colour change compares that HTML with the new
  // one and swaps only the parts that differ (#morphSection). One entry per
  // section, so at most one ~0.5 MB string is held.
  static _shownId = new Map();
  static _shownHtml = new Map();

  #rerenderTimer = null;
  #prefetchTimer = null;
  #prefetchScheduleTimer = null;
  #lastPrefetchSnapshot = null;

  constructor() {
    super();
    this.productVariants = this.getProductVariants();
    this.sectionId = `shopify-section-${this.getAttribute("section-id")}`;
    this.shortSectionId = this.getAttribute("section-id");
    this.form = this.querySelector("form");
    this.productHandle = this.#extractProductHandle();
  }

  connectedCallback() {
    // The cart form comes after this element in the page, so it is read once the page is parsed.
    const readShown = () => {
      if (ProductDetailVariation._shownId.has(this.shortSectionId)) return;
      const input = document.getElementById(this.sectionId)?.querySelector("input[data-variant-id-input]");
      const id = Number(input?.value);
      if (id) ProductDetailVariation._shownId.set(this.shortSectionId, id);
    };
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", readShown, { once: true });
    else readShown();
    this.ensureTransitionStyles();
    this.handleVariantChange();
    this.setupPrefetch();
    this.#scheduleIdlePrefetch();
  }

  #scheduleIdlePrefetch() {
    // Debounce repeated calls (e.g. rapid variant clicks). The actual prefetch
    // runs ~300ms after the last call, so we don't fire a fresh round on every
    // intermediate state.
    clearTimeout(this.#prefetchScheduleTimer);
    this.#prefetchScheduleTimer = setTimeout(() => this.#runIdlePrefetch(), 300);
  }

  #runIdlePrefetch() {
    if (!this.productHandle || !Array.isArray(this.productVariants)) return;

    // Skip on bandwidth-hostile environments
    const conn = navigator.connection;
    if (conn) {
      if (conn.saveData) return;
      if (conn.effectiveType && /2g/.test(conn.effectiveType)) return;
    }

    // Skip if the user-controlled option state hasn't changed since our last
    // prefetch — the cache already covers it.
    const opts = this.#getCurrentCheckedOptions();
    const snapshot = `${opts[1] || ""}|${opts[2] || ""}`;
    if (snapshot === this.#lastPrefetchSnapshot) return;
    this.#lastPrefetchSnapshot = snapshot;

    // Compute "one variant per fabric" candidates, prioritizing the user's
    // current size/color so the prefetched HTML is what they'd actually land on.
    const SECTION_PREFETCH_MAX = 15;
    const IMAGE_PRELOAD_MAX = 6;
    const candidates = this.#computeAdaptivePrefetchList(SECTION_PREFETCH_MAX);
    if (!candidates.length) return;

    // Image preload: limit to the top N most likely (first fabrics shown), so
    // we don't push the browser to download 3-5 MB of imagery on slow links.
    // `fetchpriority=low` keeps these from competing with critical resources.
    candidates.slice(0, IMAGE_PRELOAD_MAX).forEach((v) => this.#preloadVariantImage(v));

    // Section HTML prefetch happens during idle time
    const start = () => {
      const ids = candidates
        .map((v) => v.id)
        .filter((id) => !ProductDetailVariation._cache.has(this.#cacheKey(id)));
      this.#prefetchQueue(ids, 2);
    };

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(start, { timeout: 3000 });
    } else {
      setTimeout(start, 1500);
    }
  }

  #computeAdaptivePrefetchList(maxCount) {
    // Strategy: for each distinct option1 value (fabric), pick the variant that
    // best matches the user's currently-selected option2 (size) and option3.
    // That way prefetched HTML matches what the user will actually see when
    // they click "fabric X" — same size kept.
    if (!Array.isArray(this.productVariants)) return [];

    const currentOpts = this.#getCurrentCheckedOptions();
    const currentOption2 = currentOpts[1] || null;
    const currentOption3 = currentOpts[2] || null;
    const currentVariantId = this.#getCurrentVariantId();

    // Group available variants by their option1 value
    const byFabric = new Map();
    for (const v of this.productVariants) {
      if (!v || !v.available || !v.id) continue;
      if (!byFabric.has(v.option1)) byFabric.set(v.option1, []);
      byFabric.get(v.option1).push(v);
    }

    const result = [];
    for (const variants of byFabric.values()) {
      let chosen = null;
      if (currentOption2 !== null && currentOption3 !== null) {
        chosen = variants.find(
          (v) => v.option2 === currentOption2 && v.option3 === currentOption3
        );
      }
      if (!chosen && currentOption2 !== null) {
        chosen = variants.find((v) => v.option2 === currentOption2);
      }
      if (!chosen) chosen = variants[0];

      if (chosen && chosen.id !== currentVariantId) result.push(chosen);
      if (result.length >= maxCount) break;
    }

    // Then the other values of the CURRENT fabric, within the same cap. A product with one variant per fabric was
    // fully prefetched by the pass above, so every click was instant; a colour-variant product (Material × Farbe,
    // ~70 variants) only got one variant per fabric, and nearly every colour click waited for the server —
    // "the other templates load much faster" (Ivan, Oct 1 2026). Also covers Material × Größe sofas (sizes).
    const currentFabric = currentOpts[0];
    for (const v of byFabric.get(currentFabric) || []) {
      if (result.length >= maxCount) break;
      if (v.id !== currentVariantId && !result.includes(v)) result.push(v);
    }
    return result;
  }

  #getCurrentCheckedOptions() {
    const opts = [];
    this.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      if (!input.name || !input.name.startsWith("variant-group-")) {
        opts.push(input.value);
      }
    });
    return opts;
  }

  #getCurrentVariantId() {
    try {
      const url = new URL(window.location.href);
      const id = parseInt(url.searchParams.get("variant"), 10);
      return Number.isFinite(id) ? id : null;
    } catch {
      return null;
    }
  }

  #preloadVariantImage(variant) {
    if (!variant) return;
    // variant.featured_image is sometimes a URL string, sometimes an object {src}
    const fi = variant.featured_image;
    const rawSrc = typeof fi === "string" ? fi : fi && fi.src;
    if (!rawSrc) return;
    // Normalize protocol-relative URLs (Shopify CDN often emits "//cdn.shopify.com/...")
    const url = rawSrc.startsWith("//") ? `https:${rawSrc}` : rawSrc;
    if (ProductDetailVariation._preloadedImages.has(url)) return;
    ProductDetailVariation._preloadedImages.add(url);

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = url;
    // fetchPriority is well-supported in Chrome/Edge; ignored elsewhere
    if ("fetchPriority" in link) link.fetchPriority = "low";
    document.head.appendChild(link);
  }

  async #prefetchQueue(ids, concurrency) {
    if (!ids.length) return;
    let cursor = 0;
    const workers = Array.from({ length: Math.min(concurrency, ids.length) }, async () => {
      while (cursor < ids.length) {
        const id = ids[cursor++];
        try {
          await this.#cacheGetOrFetch(id);
        } catch {
          // Swallow — prefetch errors must never reach the user.
        }
      }
    });
    await Promise.all(workers);
  }

  #extractProductHandle() {
    // Try element attribute first (most reliable), then fall back to URL pathname.
    const fromAttr = this.getAttribute("data-product-handle");
    if (fromAttr) return fromAttr;
    const pathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return pathMatch ? pathMatch[1] : null;
  }

  #getSectionUrl(variantId) {
    if (!this.productHandle) return null;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    // A page opened on an alternate template (?view=…, e.g. an A/B variant)
    // re-renders in that template too: without it Liquid's template.suffix is
    // the product's default one, and anything built from it — the model links
    // — pointed back at the control template after the first colour change.
    const view = new URLSearchParams(window.location.search).get("view");
    const viewParam = view ? `&view=${encodeURIComponent(view)}` : "";
    return `${root}products/${this.productHandle}?variant=${variantId}&section_id=${this.shortSectionId}${viewParam}`;
  }

  #cacheKey(variantId) {
    return `${this.shortSectionId}:${variantId}`;
  }

  #cacheGetOrFetch(variantId) {
    const key = this.#cacheKey(variantId);
    let promise = ProductDetailVariation._cache.get(key);
    if (promise) {
      // Refresh LRU position
      ProductDetailVariation._cache.delete(key);
      ProductDetailVariation._cache.set(key, promise);
      return promise;
    }
    const url = this.#getSectionUrl(variantId);
    if (!url) {
      // Fallback: full-page fetch if handle couldn't be resolved
      promise = fetch(window.location.href).then((r) => r.text());
    } else {
      promise = fetch(url).then((r) => r.text());
    }
    ProductDetailVariation._cache.set(key, promise);
    // LRU eviction (Map preserves insertion order)
    if (ProductDetailVariation._cache.size > ProductDetailVariation._CACHE_MAX) {
      const oldestKey = ProductDetailVariation._cache.keys().next().value;
      ProductDetailVariation._cache.delete(oldestKey);
    }
    return promise;
  }

  setupPrefetch() {
    // Prefetch the variant HTML on hover/focus so the click feels instant.
    // Uses a 60ms debounce so we don't fire fetches for accidental sweeps.
    const onIntent = (event) => {
      const input = event.target.closest('input[type="radio"], input[type="checkbox"]');
      if (!input || input.name?.startsWith("variant-group-")) return;
      clearTimeout(this.#prefetchTimer);
      this.#prefetchTimer = setTimeout(() => {
        const candidate = this.#findVariantIfInputWereChecked(input);
        if (candidate?.id) this.#cacheGetOrFetch(candidate.id);
      }, 60);
    };
    this.addEventListener("pointerover", onIntent, { passive: true });
    this.addEventListener("focusin", onIntent);
  }

  #findVariantIfInputWereChecked(input) {
    // Build the option-values array AS IF the user had clicked `input`.
    // We collect current checked inputs but override `input.name` with `input.value`.
    const formDataArray = [];
    const radioGroups = new Map();
    this.querySelectorAll('input[type="radio"]:checked').forEach((el) => {
      if (!el.name.startsWith("variant-group-")) radioGroups.set(el.name, el.value);
    });
    radioGroups.set(input.name, input.value);
    radioGroups.forEach((v) => formDataArray.push(v));
    return this.productVariants.find((variant) =>
      formDataArray.every((value, index) => variant[`option${index + 1}`] === value)
    );
  }

  ensureTransitionStyles() {
    const styleId = "product-detail-variation-transition-styles";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      /* Cross-fade for the entire gallery container after a variant swap.
         Applied to .product-gallery__ar-wrapper so every slide (hero +
         thumbnails + videos) fades in together as one smooth transition.
         Class is removed on the next frame so it doesn't linger and
         clobber later state. */
      .product-gallery-swap-in {
        animation: pdv-fade-in var(--transition-duration, 180ms) var(--transition-ease, ease) both;
      }

      @keyframes pdv-fade-in {
        from { opacity: 0; }
        to   { opacity: 1; }
      }

      @media (prefers-reduced-motion: reduce) {
        .product-gallery-swap-in { animation: none; }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Wait until the new featured image has decoded its bitmap (so the browser
   * can paint it the instant we swap the DOM). Caps at 120ms so cold paths
   * aren't visibly delayed — the cross-fade keyframe still hides any residual
   * gap on slow networks.
   *
   * Returns a resolved Promise even when decode rejects, so callers can
   * always `await` without try/catch.
   */
  #waitForFeaturedImageDecode(newSection) {
    // A gallery that carries every colour's photo list ([data-kc-gallery-sets],
    // the gallery block's instant_gallery — Luma v2 only) has already switched
    // its first five photos on click. The swap below replaces them with NEW
    // <img> elements, and a new element shows nothing until its photo is
    // decoded: photos 2-5 flashed white while the main one was fine. There,
    // wait for all five (up to 1s); the page keeps showing the photos it has
    // meanwhile. Every other gallery keeps the original: the first photo, 120ms.
    const allFive = !!newSection.querySelector("[data-kc-gallery-sets]");
    const images = Array.from(
      newSection.querySelectorAll(".product-gallery__media-list .product-gallery__media img")
    )
      .slice(0, allFive ? 5 : 1)
      .filter((img) => img.getAttribute("src"));
    if (!images.length) return Promise.resolve();

    const decodes = images.map((newImg) => {
      const probe = new Image();
      if (newImg.getAttribute("srcset")) probe.srcset = newImg.getAttribute("srcset");
      if (newImg.getAttribute("sizes")) probe.sizes = newImg.getAttribute("sizes");
      probe.src = newImg.getAttribute("src");
      return probe.decode().catch(() => {});
    });
    const timeout = new Promise((resolve) => setTimeout(resolve, allFive ? 1000 : 120));
    return Promise.race([Promise.all(decodes), timeout]);
  }

  getProductVariants() {
    try {
      const scriptElement = this.querySelector(
        'script[type="application/json"]'
      );
      if (!scriptElement) {
        console.error("Product variants script not found");
        return [];
      }
      const productVariants = scriptElement.textContent;
      return JSON.parse(productVariants);
    } catch (error) {
      console.error("Error parsing product variants", error);
      return [];
    }
  }

  /**
   * Public, for option controls that live OUTSIDE this element (the colour
   * sheet, assets/kc-fabric-sheet.js): the variant that option `name` taking
   * `value` would select, and a warm-up of that variant's section HTML so the
   * real change finds it in the cache and skips the 150ms cold-path debounce.
   */
  variantForOption(name, value) {
    return this.#findVariantIfInputWereChecked({ name, value });
  }

  prefetchVariant(variantId) {
    if (!variantId) return;
    this.#cacheGetOrFetch(variantId).catch(() => {
      ProductDetailVariation._cache.delete(this.#cacheKey(variantId));
    });
  }

  handleVariantChange() {
    this.addEventListener("change", this.handleFormData.bind(this));
  }

  handleFormData(event) {
    const formDataArray = [];
    const radioButtons = this.querySelectorAll('input[type="radio"]:checked');

    radioButtons.forEach((input) => {
      if (!input.name.startsWith("variant-group-")) {
        formDataArray.push(input.value);
      }
    });

    const result = this.productVariants.find((variant) => {
      return formDataArray.every(
        (value, index) => variant[`option${index + 1}`] === value
      );
    });

    // The picked combination does not exist — e.g. the colour-variant products (Material × Farbe): Nata is a Cord
    // colour, so clicking Bouclé while Nata was selected asked for "Bouclé / Nata" and nothing happened; the customer
    // could not change the fabric at all (Ivan, Oct 1 2026). Take the closest variant that HAS the value just clicked:
    // keep as many of the other options as possible, prefer one in stock. Only when the whole variant list is inline
    // (< 250) — past Liquid's cap a missing match means "not listed", which the lookup below resolves.
    if (!result && this.productVariants.length < 250) {
      const optionInputs = Array.from(radioButtons).filter((input) => !input.name.startsWith("variant-group-"));
      const changed = event?.target;
      const optionIndex = changed ? optionInputs.indexOf(changed) : -1;
      if (optionIndex >= 0) {
        const key = `option${optionIndex + 1}`;
        const score = (variant) =>
          formDataArray.reduce((n, value, i) => n + (variant[`option${i + 1}`] === value ? 1 : 0), 0) +
          (variant.available ? 0.5 : 0);
        const closest = this.productVariants
          .filter((variant) => variant[key] === changed.value)
          .sort((a, b) => score(b) - score(a))[0];
        if (closest) {
          this.#applyVariant(closest);
          return;
        }
      }
    }

    if (!result) {
      this.#resolveBeyondVariantCap(formDataArray).then((resolved) => {
        if (resolved) this.#applyVariant(resolved);
      });
      return;
    }

    this.#applyVariant(result);
  }

  /**
   * Liquid's `product.variants` stops at 250, so on a bigger product (Terra L
   * 4-Zits has 264) the inline JSON is missing the tail and find() comes back
   * empty: the swatch looks selected but price, gallery and cart stay on the
   * previous variant. Let Shopify resolve the combination from its option-value
   * ids (`?option_values=`) and read the variant id off the returned section.
   */
  async #resolveBeyondVariantCap(values) {
    const checkedIds = () =>
      Array.from(this.querySelectorAll('input[type="radio"]:checked'))
        .filter((input) => !input.name.startsWith("variant-group-"))
        .map((input) => input.dataset.optionValueId);
    const ids = checkedIds();
    if (!this.productHandle || !ids.length || ids.some((id) => !id)) return null;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    const url = `${root}products/${this.productHandle}?option_values=${ids.join(",")}&section_id=${this.shortSectionId}`;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const frag = document.createRange().createContextualFragment(html);
      const id = Number(frag.querySelector("input[data-variant-id-input]")?.value);
      if (!id) return null;

      // The customer picked something else while this was in flight.
      if (checkedIds().join(",") !== ids.join(",")) return null;

      const variant = { id, title: values.join(" / "), available: true };
      values.forEach((value, index) => (variant[`option${index + 1}`] = value));
      this.productVariants.push(variant);
      ProductDetailVariation._cache.set(this.#cacheKey(id), Promise.resolve(html));
      return variant;
    } catch (err) {
      console.error("Variant resolve failed:", err);
      return null;
    }
  }

  #applyVariant(result) {
    // Dispatch variant:change event before rerendering (like theme.js does)
    if (result) {
      const selectedOptions = [];
      if (result.option1)
        selectedOptions.push({ name: "Option1", value: result.option1 });
      if (result.option2)
        selectedOptions.push({ name: "Option2", value: result.option2 });
      if (result.option3)
        selectedOptions.push({ name: "Option3", value: result.option3 });

      this.dispatchEvent(
        new CustomEvent("variant:change", {
          bubbles: true,
          detail: {
            variant: {
              id: result.id,
              title: result.title,
              selected_options: selectedOptions,
              option1: result.option1,
              option2: result.option2,
              option3: result.option3,
            },
          },
        })
      );
    }

    this.updateUrlWithVariant(result);
    clearTimeout(this.#rerenderTimer);

    // If we've already prefetched (or previously rendered) this variant, fire the
    // rerender synchronously: no fetch is needed, so the 150ms anti-rapid-click
    // debounce only adds latency. The debounce still kicks in for cold variants.
    if (result && ProductDetailVariation._cache.has(this.#cacheKey(result.id))) {
      this.rerenderContent(result);
    } else {
      this.#rerenderTimer = setTimeout(() => {
        this.rerenderContent(result);
      }, 150);
    }

    // The set of "likely-next fabrics" depends on the user's current size/color.
    // When they change size, recompute and prefetch the new (fabric × size)
    // combinations in the background. Internally debounced + dedup'd by snapshot.
    this.#scheduleIdlePrefetch();
  }

  async rerenderContent(variant) {
    if (!variant) return;
    const currentSection = document.getElementById(this.sectionId);

    try {
      // Use Shopify's section rendering endpoint instead of the full page:
      // ~30 KB instead of ~300 KB, and the response is the section HTML only.
      // Cache key is `sectionId:variantId` so repeat visits to the same variant
      // are 0-network.
      const htmlPromise = this.#cacheGetOrFetch(variant.id);
      const html = await htmlPromise;

      // First change on this page: fetch the section as the server renders it
      // for the variant on screen, as the base for the comparison below.
      const shownKey = this.shortSectionId;
      if (!ProductDetailVariation._shownHtml.has(shownKey)) {
        const shownId = ProductDetailVariation._shownId.get(shownKey);
        if (shownId) {
          const base = await this.#cacheGetOrFetch(shownId).catch(() => null);
          if (base && !ProductDetailVariation._shownHtml.has(shownKey)) {
            ProductDetailVariation._shownHtml.set(shownKey, base);
          }
        }
      }

      // createContextualFragment is ~30-50% faster than DOMParser + has lower
      // memory overhead since it doesn't build a full HTMLDocument with
      // <html>/<head>/<body>. Scripts inside the fragment stay inert until
      // inserted into the live DOM, same as DOMParser.
      const frag = document.createRange().createContextualFragment(html);
      const newSection = frag.querySelector(`#${this.sectionId}`);

      if (newSection && currentSection) {
        // Wait for the new featured image to decode BEFORE swapping the DOM.
        // Without this wait, replaceChildren() removes the old <img> while the
        // new <img> has no decoded bitmap yet → the section's white background
        // bleeds through. Capped at 120ms so cold paths aren't visibly delayed;
        // the cross-fade still hides any residual gap.
        await this.#waitForFeaturedImageDecode(newSection);

        // Read synchronously right before the swap: a render that landed in
        // the meantime has already updated what the page shows.
        const baseHtml = ProductDetailVariation._shownHtml.get(this.shortSectionId);
        let morphed = false;
        if (baseHtml) {
          const backup = newSection.cloneNode(true);
          try {
            const baseSection = document.createRange().createContextualFragment(baseHtml).querySelector(`#${this.sectionId}`);
            morphed = !!baseSection && this.#morphSection(currentSection, baseSection, newSection);
          } catch (err) {
            console.error("Variant morph failed, full swap instead:", err);
            this.mergeSection(currentSection, backup);
            morphed = true;
          }
          if (!morphed) this.mergeSection(currentSection, backup);
        } else {
          this.mergeSection(currentSection, newSection);
        }
        ProductDetailVariation._shownHtml.set(this.shortSectionId, html);
        ProductDetailVariation._shownId.set(this.shortSectionId, variant.id);

        // Cross-fade the whole gallery container (hero + thumbnails +
        // videos) so the change reads as one smooth transition, not as
        // a stack of independently-replaced slides. Class is removed on
        // the next frame so it doesn't linger and clobber later renders.
        const galleryWrapper = currentSection.querySelector(
          ".product-gallery__ar-wrapper"
        );
        if (galleryWrapper) {
          galleryWrapper.classList.add("product-gallery-swap-in");
          requestAnimationFrame(() => {
            galleryWrapper.classList.remove("product-gallery-swap-in");
          });
        }

        document.dispatchEvent(
          new CustomEvent("variant:rerendered", {
            detail: {
              sectionId: this.sectionId,
              variantId: variant.id,
            },
          })
        );
      }
    } catch (err) {
      // If the cached promise rejected (network error), evict so the next
      // click retries instead of replaying the failure forever.
      ProductDetailVariation._cache.delete(this.#cacheKey(variant.id));
      console.error("Variant rerender failed:", err);
    }
  }

  /**
   * Swap only what changed. `base` is the section as the server rendered it
   * for the variant on screen, `next` the one for the new variant. Where the
   * two are identical the live element is left alone: no new <img>, no
   * re-injected stars, Klarna box or countdown, so nothing blinks. Where they
   * differ, the walk goes down while the structure matches and replaces the
   * smallest element that changed (custom elements, scripts, images and form
   * fields always as a whole: they set themselves up when inserted).
   *
   * Inline scripts that are not replaced do not run again, so one runs again
   * when the element right before it was replaced (scripts that bind to
   * document.currentScript.previousElementSibling, e.g. the accordions) or
   * when its text names a replaced element's id or step key. Elements marked
   * data-preserve-on-rerender are restored as copies exactly as before.
   * Returns false if the section itself does not line up (caller swaps all).
   */
  #morphSection(currentSection, base, next) {
    const LEAF = /^(SCRIPT|STYLE|TEMPLATE|NOSCRIPT|INPUT|SELECT|TEXTAREA|OPTION|BUTTON|IMG|PICTURE|SOURCE|VIDEO|IFRAME|CANVAS|SVG)$/i;
    // <product-rerender> only wraps the product and listens on the form: safe to look inside.
    const WRAPPER = /^(PRODUCT-RERENDER)$/;
    const isLeaf = (el) => (el.tagName.includes("-") && !WRAPPER.test(el.tagName)) || LEAF.test(el.tagName);
    const sameKind = (x, y) => x.tagName === y.tagName && x.getAttribute("id") === y.getAttribute("id");
    // A preserved element (the open colour sheet) is the full element on the page but only a
    // placeholder in a re-render: same id and both preserved counts as the same element.
    const liveKind = (live, a) => sameKind(live, a) || (live.hasAttribute("data-preserve-on-rerender") && a.hasAttribute("data-preserve-on-rerender") && live.getAttribute("id") === a.getAttribute("id"));
    const ownText = (n) => {
      let t = "";
      for (const c of n.childNodes) if (c.nodeType === 3) t += c.data;
      return t;
    };
    const lineUp = (live, a, b) => {
      if (isLeaf(a) || !sameKind(a, b) || !liveKind(live, a)) return false;
      const ac = a.children, bc = b.children, lc = live.children;
      if (!ac.length || ac.length !== bc.length || lc.length !== ac.length) return false;
      if (ownText(a) !== ownText(b)) return false;
      for (let i = 0; i < ac.length; i++) {
        if (!sameKind(ac[i], bc[i]) || !liveKind(lc[i], ac[i])) return false;
      }
      return true;
    };
    if (!lineUp(currentSection, base, next)) return false;

    // Copies of the preserved elements, taken before anything moves (as mergeSection does).
    const preserved = new Map();
    currentSection.querySelectorAll("[data-preserve-on-rerender]").forEach((el) => {
      const id = el.getAttribute("id") || el.getAttribute("data-preserve-id");
      if (id) preserved.set(id, el.cloneNode(true));
    });

    const replaced = [];
    const walk = (live, a, b) => {
      if (live.hasAttribute("data-preserve-on-rerender")) return;
      if (a.isEqualNode(b)) return;
      if (!lineUp(live, a, b)) {
        live.replaceWith(b);
        replaced.push(b);
        return;
      }
      // Only the attributes the server changed: classes and state that
      // scripts set on the live element stay.
      for (const at of Array.from(b.attributes)) {
        if (a.getAttribute(at.name) !== at.value) live.setAttribute(at.name, at.value);
      }
      for (const at of Array.from(a.attributes)) {
        if (!b.hasAttribute(at.name)) live.removeAttribute(at.name);
      }
      const A = Array.from(a.children), B = Array.from(b.children), L = Array.from(live.children);
      for (let i = 0; i < A.length; i++) walk(L[i], A[i], B[i]);
    };
    for (const at of Array.from(next.attributes)) {
      if (base.getAttribute(at.name) !== at.value) currentSection.setAttribute(at.name, at.value);
    }
    const A = Array.from(base.children), B = Array.from(next.children), L = Array.from(currentSection.children);
    for (let i = 0; i < A.length; i++) walk(L[i], A[i], B[i]);

    // Inline scripts that stayed but belong to something that was replaced: run them again.
    if (replaced.length) {
      const replacedSet = new Set(replaced);
      const keys = new Set();
      replaced.forEach((el) => {
        [el, ...el.querySelectorAll("[id], [data-kc-open-key]")].forEach((node) => {
          ["id", "data-kc-open-key"].forEach((name) => {
            const value = node.getAttribute(name);
            if (value && value.length >= 8) keys.add(value);
          });
        });
      });
      const keyList = Array.from(keys);
      const insideReplaced = (node) => replaced.some((el) => el === node || el.contains(node));
      currentSection.querySelectorAll("script:not([src])").forEach((script) => {
        const type = (script.getAttribute("type") || "").toLowerCase();
        if (type && type !== "text/javascript" && type !== "module") return;
        if (insideReplaced(script)) return;
        let prev = script.previousElementSibling;
        while (prev && (prev.tagName === "SCRIPT" || prev.tagName === "STYLE")) prev = prev.previousElementSibling;
        const text = script.textContent;
        const tied = (prev && replacedSet.has(prev)) || keyList.some((key) => text.includes(key));
        if (!tied) return;
        const fresh = document.createElement("script");
        for (const at of Array.from(script.attributes)) fresh.setAttribute(at.name, at.value);
        fresh.textContent = text;
        script.replaceWith(fresh);
      });
    }

    // Preserved elements: the same copy-and-restore as mergeSection.
    preserved.forEach((copy, id) => {
      const target = currentSection.querySelector(`[id="${id}"], [data-preserve-id="${id}"]`);
      if (target && target.parentNode) target.parentNode.replaceChild(copy, target);
    });
    return true;
  }

  mergeSection(currentSection, newSection) {
    // Preserve elements that should not be re-rendered
    const preservedElements = currentSection.querySelectorAll(
      "[data-preserve-on-rerender]"
    );
    const preservedMap = new Map();

    preservedElements.forEach((el) => {
      const id = el.getAttribute("id") || el.getAttribute("data-preserve-id");
      if (id) {
        preservedMap.set(id, el.cloneNode(true));
      }
    });

    // Replace all children
    currentSection.replaceChildren(...newSection.children);

    // Restore preserved elements
    preservedMap.forEach((preservedEl, id) => {
      const targetEl = currentSection.querySelector(
        `[id="${id}"], [data-preserve-id="${id}"]`
      );
      if (targetEl && targetEl.parentNode) {
        targetEl.parentNode.replaceChild(preservedEl, targetEl);
      }
    });
  }

  updateUrlWithVariant(variant) {
    if (variant && variant.id) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("variant", variant.id);
      window.history.replaceState(
        { path: currentUrl.toString() },
        "",
        currentUrl.toString()
      );
    }
  }
}

if (!customElements.get("product-detail-variation")) {
  customElements.define("product-detail-variation", ProductDetailVariation);
}

/**
 * Product detail variant group
 */
class ProductDetailVariantGroup extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    const selectElement = this.querySelector("select");
    if (selectElement) {
      selectElement.addEventListener("change", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.updateVariantItem(event.target.value);
      });
    }

    if (this.querySelector('input[type="radio"]')) {
      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.addEventListener("change", (event) => {
          if (event.target.name.startsWith("variant-group-")) {
            event.preventDefault();
            event.stopPropagation();
            this.updateVariantItem(event.target.value);
          }
        });
      });
    }
  }

  updateVariantItem(group) {
    const parentElement = this.closest(".kc-product-detail-variant-picker");
    if (parentElement) {
      const groupTitle = parentElement.querySelector(
        ".variant-list-title-group"
      );
      if (groupTitle) {
        const titleValue = groupTitle.querySelector(
          ".variant-list-title-value"
        );
        if (titleValue) {
          titleValue.textContent = group;
        }
      }

      parentElement.querySelectorAll(".variant-color").forEach((item) => {
        if (item.dataset.group === group) {
          item.removeAttribute("hidden");
        } else {
          item.setAttribute("hidden", "");
        }
      });

      // When a fabric/group is picked, immediately select the FIRST available
      // color of that group and trigger a real variant change so the price (and
      // the rest of the PDP) updates to that color. Without this the group swap
      // only changes which swatches are visible — no color is checked and the
      // price stays on the previously-selected (now-hidden) color.
      this.selectFirstColorInGroup(parentElement, group);
    }
  }

  selectFirstColorInGroup(parentElement, group) {
    // Find the FIRST color swatch that belongs to the just-selected fabric.
    // Prefer the first *available* one; fall back to the very first swatch if
    // the whole group is sold out so the UI still reflects the chosen fabric.
    const allSwatches = Array.from(
      parentElement.querySelectorAll(`.variant-color[data-group="${group}"]`)
    );
    if (!allSwatches.length) return;

    const pickInput = (swatch) =>
      swatch && swatch.querySelector('input[type="radio"]');

    let targetSwatch =
      allSwatches.find((s) => !s.classList.contains("disabled")) ||
      allSwatches[0];
    const targetInput = pickInput(targetSwatch);
    if (!targetInput) return;

    // If that first swatch is ALREADY the selected one, there's nothing to do —
    // we're already showing the fabric's first color. Otherwise select it and
    // fire a real change so the host <product-detail-variation> recomputes the
    // variant / price / gallery. Checking it auto-unchecks the previously
    // selected color since every swatch shares the same radio `name`.
    if (targetInput.checked) return;

    targetInput.checked = true;
    targetInput.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

if (!customElements.get("product-detail-variant-group")) {
  customElements.define(
    "product-detail-variant-group",
    ProductDetailVariantGroup
  );
}

/**
 * Product detail product related
 */
class ProductDetailProductRelated extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.handleRelatedChange();
  }

  handleRelatedChange() {
    const selectElement = this.querySelector("select");
    if (selectElement) {
      selectElement.addEventListener("change", (event) => {
        const url = event.target.value;
        window.location.href = url;
      });
    }
  }
}

if (!customElements.get("product-detail-product-related")) {
  customElements.define(
    "product-detail-product-related",
    ProductDetailProductRelated
  );
}
